import { useEffect, useState, useRef, useCallback } from "react";
import api from "../../api/client";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

function ProductThumb({ url, nombre }) {
  const [loaded, setLoaded] = useState(false);
  if (!url) return <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-300 text-lg">📷</div>;
  return (
    <div className="relative w-10 h-10">
      {!loaded && <div className="absolute inset-0 rounded-lg bg-gray-100 animate-pulse" />}
      <img
        src={`${API_BASE}${url}`}
        alt={nombre}
        className={`w-10 h-10 rounded-lg object-cover transition-opacity duration-200 ${loaded ? "opacity-100" : "opacity-0"}`}
        onLoad={() => setLoaded(true)}
      />
    </div>
  );
}

function Modal({ open, title, onClose, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xl my-4">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

const EMPTY = {
  nombre: "", descripcion: "", categoria_id: "", precio: "",
  precio_promocion: "", inventario: "", activo_menu: true, orden: 0, variantes: [],
};

function resizeFoto(file, maxPx = 800) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const ratio = Math.min(maxPx / img.naturalWidth, maxPx / img.naturalHeight, 1);
      const w = Math.round(img.naturalWidth * ratio);
      const h = Math.round(img.naturalHeight * ratio);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);
      canvas.toBlob(resolve, "image/jpeg", 0.88);
    };
    img.src = url;
  });
}

const FILTROS_VACIOS = { nombre: "", categoria_id: "", precio_min: "", precio_max: "" };

export default function Productos() {
  const [prods, setProds] = useState([]);
  const [cats, setCats] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [limpiarFoto, setLimpiarFoto] = useState(false);
  const fileRef = useRef();
  const [iaLoading, setIaLoading] = useState(false);
  const [sugerencias, setSugerencias] = useState([]);

  // Filtros
  const [filtros, setFiltros] = useState(FILTROS_VACIOS);
  const [filtrosActivos, setFiltrosActivos] = useState(FILTROS_VACIOS);

  // CSV import
  const csvRef = useRef();
  const [importando, setImportando] = useState(false);
  const [resultadoImport, setResultadoImport] = useState(null);
  const [modalImport, setModalImport] = useState(false);
  const [plan, setPlan] = useState("gratis");

  const PAGE_SIZE = 10;

  const cargar = useCallback((filtrosParam = filtrosActivos, pagina = page) => {
    setLoading(true);
    const params = new URLSearchParams({ page: pagina, page_size: PAGE_SIZE });
    if (filtrosParam.nombre)      params.append("nombre", filtrosParam.nombre);
    if (filtrosParam.categoria_id) params.append("categoria_id", filtrosParam.categoria_id);
    if (filtrosParam.precio_min)  params.append("precio_min", filtrosParam.precio_min);
    if (filtrosParam.precio_max)  params.append("precio_max", filtrosParam.precio_max);

    api.get(`/productos/?${params.toString()}`)
      .then((r) => { setProds(r.data.items); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  }, [filtrosActivos, page]);

  useEffect(() => { cargar(filtrosActivos, page); }, [page, filtrosActivos]);

  useEffect(() => {
    api.get("/categorias/todas").then((r) => setCats(r.data));
    api.get("/suscripcion/").then((r) => setPlan(r.data.plan || "gratis")).catch(() => {});
  }, []);

  const aplicarFiltros = () => {
    setFiltrosActivos({ ...filtros });
    setPage(1);
  };

  const limpiarFiltros = () => {
    setFiltros(FILTROS_VACIOS);
    setFiltrosActivos(FILTROS_VACIOS);
    setPage(1);
  };

  const hayFiltros = Object.values(filtrosActivos).some(Boolean);

  const sugerirDescripcion = async () => {
    if (!form.nombre.trim()) return;
    setIaLoading(true);
    setSugerencias([]);
    try {
      const cat = cats.find(c => String(c.id) === String(form.categoria_id));
      const r = await api.post("/ia/descripcion", { nombre: form.nombre, categoria: cat?.nombre || null });
      setSugerencias(r.data.sugerencias || []);
    } catch {
      setSugerencias([]);
    } finally {
      setIaLoading(false);
    }
  };

  const mejorarDescripcion = async () => {
    if (!form.nombre.trim() || !form.descripcion.trim()) return;
    setIaLoading(true);
    setSugerencias([]);
    try {
      const cat = cats.find(c => String(c.id) === String(form.categoria_id));
      const r = await api.post("/ia/mejorar", { nombre: form.nombre, descripcion: form.descripcion, categoria: cat?.nombre || null });
      setSugerencias(r.data.sugerencias || []);
    } catch {
      setSugerencias([]);
    } finally {
      setIaLoading(false);
    }
  };

  const openNew = () => {
    const nextOrden = prods.length > 0 ? Math.max(...prods.map(p => p.orden ?? 0)) + 1 : 0;
    setForm({ ...EMPTY, orden: nextOrden });
    setEditId(null); setError(""); setPreview(null); setLimpiarFoto(false); setSugerencias([]); setModal(true);
  };
  const openEdit = (p) => {
    setForm({
      nombre: p.nombre, descripcion: p.descripcion || "", categoria_id: p.categoria_id || "",
      precio: p.precio, precio_promocion: p.precio_promocion || "", inventario: p.inventario ?? "",
      activo_menu: p.activo_menu, orden: p.orden,
      variantes: (p.variantes || []).map(v => ({ ...v, _opInput: "" })),
    });
    setPreview(p.foto_url ? `${API_BASE}${p.foto_url}` : null);
    setLimpiarFoto(false);
    setEditId(p.id); setError(""); setSugerencias([]); setModal(true);
  };

  const guardar = async (e) => {
    e.preventDefault();
    setError("");
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (k === "variantes") return;
      if (v !== "" && v !== null && v !== undefined) fd.append(k, v);
    });
    const cleanVariantes = (form.variantes || [])
      .filter(v => v.nombre && v.opciones && v.opciones.length > 0)
      .map(({ nombre, opciones }) => ({ nombre, opciones }));
    if (cleanVariantes.length > 0) fd.append("variantes", JSON.stringify(cleanVariantes));

    const fotoFile = fileRef.current?.files[0];
    if (fotoFile) {
      const resized = await resizeFoto(fotoFile);
      fd.append("foto", resized, "foto.jpg");
    }

    if (editId) {
      if (limpiarFoto) fd.append("borrar_foto", "true");
      if (form.precio_promocion === "") fd.append("limpiar_promo", "true");
      if (form.inventario === "") fd.append("limpiar_inventario", "true");
    }

    try {
      if (editId) {
        await api.put(`/productos/${editId}`, fd);
      } else {
        await api.post("/productos/", fd);
      }
      setModal(false);
      cargar();
    } catch (err) {
      setError(err.response?.data?.detail || "Error al guardar");
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar este producto?")) return;
    try {
      await api.delete(`/productos/${id}`);
      cargar();
    } catch (err) {
      alert(err.response?.data?.detail || "No se pudo eliminar el producto");
    }
  };

  const toggleActivo = async (p) => {
    const fd = new FormData();
    fd.append("activo_menu", !p.activo_menu);
    await api.put(`/productos/${p.id}`, fd);
    cargar();
  };

  const descargarPlantilla = async () => {
    try {
      const r = await api.get("/productos/plantilla-csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "plantilla_productos.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      alert("No se pudo descargar la plantilla");
    }
  };

  const importarCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportando(true);
    setResultadoImport(null);
    try {
      const fd = new FormData();
      fd.append("archivo", file);
      const r = await api.post("/productos/importar-csv", fd);
      setResultadoImport(r.data);
      setModalImport(true);
      cargar();
    } catch (err) {
      setResultadoImport({ error: err.response?.data?.detail || "Error al importar" });
      setModalImport(true);
    } finally {
      setImportando(false);
      e.target.value = "";
    }
  };

  const addPreset = (nombre, opciones) => {
    setForm(f => {
      const ya = (f.variantes || []).find(v => v.nombre.toLowerCase() === nombre.toLowerCase());
      if (ya) return f;
      return { ...f, variantes: [...(f.variantes || []), { nombre, opciones, _opInput: "" }] };
    });
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Productos</h2>
          <p className="text-gray-500 text-sm mt-1">
            {total} producto{total !== 1 ? "s" : ""}{hayFiltros ? " (filtrados)" : " en total"}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={descargarPlantilla}
            className="border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm font-medium px-3 py-2 rounded-lg transition-colors flex items-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Plantilla CSV
          </button>
          <button
            onClick={() => {
              if (plan === "gratis") {
                alert("Importar CSV está disponible en los planes Emprendedor y Empresa. Actualiza tu suscripción para acceder a esta función.");
                return;
              }
              csvRef.current?.click();
            }}
            disabled={importando}
            title={plan === "gratis" ? "Disponible en plan Emprendedor o Empresa" : "Importar productos desde CSV"}
            className={`border text-sm font-medium px-3 py-2 rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50
              ${plan === "gratis"
                ? "border-gray-200 text-gray-400 cursor-not-allowed"
                : "border-indigo-300 text-indigo-600 hover:bg-indigo-50"}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l4-4m0 0l4 4m-4-4v12" />
            </svg>
            {importando ? "Importando..." : plan === "gratis" ? "🔒 Importar CSV" : "Importar CSV"}
          </button>
          <input ref={csvRef} type="file" accept=".csv" className="hidden" onChange={importarCSV} />
          <button onClick={openNew} className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
            + Nuevo producto
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Buscar por nombre</label>
            <input
              value={filtros.nombre}
              onChange={(e) => setFiltros(f => ({ ...f, nombre: e.target.value }))}
              onKeyDown={(e) => e.key === "Enter" && aplicarFiltros()}
              placeholder="Ej: Pizza, Hamburguesa..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Categoria</label>
            <select
              value={filtros.categoria_id}
              onChange={(e) => setFiltros(f => ({ ...f, categoria_id: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Todas las categorias</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Precio minimo $</label>
            <input
              type="number" min="0" step="0.01"
              value={filtros.precio_min}
              onChange={(e) => setFiltros(f => ({ ...f, precio_min: e.target.value }))}
              onKeyDown={(e) => e.key === "Enter" && aplicarFiltros()}
              placeholder="0.00"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Precio maximo $</label>
            <input
              type="number" min="0" step="0.01"
              value={filtros.precio_max}
              onChange={(e) => setFiltros(f => ({ ...f, precio_max: e.target.value }))}
              onKeyDown={(e) => e.key === "Enter" && aplicarFiltros()}
              placeholder="999.99"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={aplicarFiltros}
            className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            Buscar
          </button>
          {hayFiltros && (
            <button
              onClick={limpiarFiltros}
              className="border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
            >
              Limpiar filtros
            </button>
          )}
          {hayFiltros && (
            <span className="text-xs text-indigo-600 font-medium">
              {total} resultado{total !== 1 ? "s" : ""}
            </span>
          )}
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : prods.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <div className="text-5xl mb-3">🛍️</div>
            <p className="font-medium">{hayFiltros ? "Sin resultados para los filtros aplicados" : "No hay productos aun"}</p>
            {hayFiltros
              ? <button onClick={limpiarFiltros} className="text-sm text-indigo-600 mt-2 hover:underline">Limpiar filtros</button>
              : <p className="text-sm mt-1">Agrega tu primer producto</p>
            }
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
              <tr>
                <th className="px-4 py-3 text-left">Foto</th>
                <th className="px-4 py-3 text-left">Nombre</th>
                <th className="px-4 py-3 text-left hidden md:table-cell">Categoria</th>
                <th className="px-4 py-3 text-right">Precio</th>
                <th className="px-4 py-3 text-center hidden sm:table-cell">Inventario</th>
                <th className="px-4 py-3 text-center hidden lg:table-cell">Orden</th>
                <th className="px-4 py-3 text-center">Activo</th>
                <th className="px-4 py-3 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {prods.map((p) => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <ProductThumb url={p.foto_url} nombre={p.nombre} />
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-800">{p.nombre}</p>
                    {p.precio_promocion && (
                      <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded">Promo</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-500 hidden md:table-cell">{p.categoria?.nombre || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <p className="font-semibold text-gray-800">${Number(p.precio).toFixed(2)}</p>
                    {p.precio_promocion && <p className="text-xs text-red-500">${Number(p.precio_promocion).toFixed(2)}</p>}
                  </td>
                  <td className="px-4 py-3 text-center text-gray-600 hidden sm:table-cell">
                    {p.inventario ?? "∞"}
                  </td>
                  <td className="px-4 py-3 text-center text-gray-400 text-xs hidden lg:table-cell">
                    {p.orden}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => toggleActivo(p)}
                      className={`w-11 h-6 rounded-full transition-colors relative ${p.activo_menu ? "bg-green-500" : "bg-gray-300"}`}
                    >
                      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${p.activo_menu ? "left-5" : "left-0.5"}`} />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => openEdit(p)} className="text-indigo-600 hover:text-indigo-800 text-xs font-medium">Editar</button>
                      <button onClick={() => eliminar(p.id)} className="text-red-500 hover:text-red-700 text-xs font-medium">Eliminar</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t text-sm text-gray-500">
            <span>Pagina {page} de {totalPages}</span>
            <div className="flex gap-2">
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border rounded disabled:opacity-40">Anterior</button>
              <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border rounded disabled:opacity-40">Siguiente</button>
            </div>
          </div>
        )}
      </div>

      {/* Modal resultado importacion */}
      <Modal open={modalImport} title="Resultado de importacion" onClose={() => setModalImport(false)}>
        {resultadoImport?.error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{resultadoImport.error}</div>
        ) : resultadoImport && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-green-700">{resultadoImport.creados}</p>
                <p className="text-xs text-green-600 font-medium">Productos creados</p>
              </div>
              <div className={`border rounded-lg p-3 text-center ${resultadoImport.total_errores > 0 ? "bg-red-50 border-red-200" : "bg-gray-50 border-gray-200"}`}>
                <p className={`text-2xl font-bold ${resultadoImport.total_errores > 0 ? "text-red-700" : "text-gray-500"}`}>{resultadoImport.total_errores}</p>
                <p className={`text-xs font-medium ${resultadoImport.total_errores > 0 ? "text-red-600" : "text-gray-500"}`}>Errores</p>
              </div>
            </div>
            {resultadoImport.errores?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Detalle de errores:</p>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {resultadoImport.errores.map((e, i) => (
                    <div key={i} className="text-xs bg-red-50 border border-red-100 rounded px-3 py-1.5 text-red-700">
                      Fila {e.fila}: {e.error}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <button onClick={() => setModalImport(false)} className="w-full bg-indigo-600 text-white py-2 rounded-lg text-sm hover:bg-indigo-700">
              Cerrar
            </button>
          </div>
        )}
      </Modal>

      {/* Modal producto */}
      <Modal open={modal} title={editId ? "Editar producto" : "Nuevo producto"} onClose={() => setModal(false)}>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm mb-3">{error}</div>}
        <form onSubmit={guardar} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
            <input required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Nombre del producto" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">Descripcion</label>
              <div className="flex items-center gap-1.5">
                {form.descripcion.trim() && (
                  <button
                    type="button"
                    onClick={mejorarDescripcion}
                    disabled={iaLoading || !form.nombre.trim()}
                    className="flex items-center gap-1 text-xs bg-amber-50 hover:bg-amber-100 text-amber-700 font-medium px-2.5 py-1 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {iaLoading ? "..." : "✨ Mejorar"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={sugerirDescripcion}
                  disabled={iaLoading || !form.nombre.trim()}
                  className="flex items-center gap-1 text-xs bg-violet-50 hover:bg-violet-100 text-violet-700 font-medium px-2.5 py-1 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {iaLoading ? (
                    <span className="flex items-center gap-1">
                      <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                      </svg>
                      Generando...
                    </span>
                  ) : "✨ Sugerir"}
                </button>
              </div>
            </div>
            <textarea value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none" />
            {sugerencias.length > 0 && (
              <div className="mt-2 space-y-1.5">
                <p className="text-xs text-gray-500 font-medium">Elige una sugerencia:</p>
                {sugerencias.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => { setForm(f => ({ ...f, descripcion: s })); setSugerencias([]); }}
                    className="w-full text-left text-xs bg-violet-50 hover:bg-violet-100 border border-violet-200 text-gray-700 px-3 py-2 rounded-lg transition-colors leading-relaxed"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
            <select value={form.categoria_id} onChange={(e) => setForm({ ...form, categoria_id: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
              <option value="">Sin categoria</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Precio $ *</label>
              <input required type="number" step="0.01" min="0" value={form.precio} onChange={(e) => setForm({ ...form, precio: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="0.00" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Precio promo $</label>
              <input type="number" step="0.01" min="0" value={form.precio_promocion} onChange={(e) => setForm({ ...form, precio_promocion: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="0.00" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Inventario (opcional)</label>
              <input type="number" min="0" value={form.inventario} onChange={(e) => setForm({ ...form, inventario: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="∞" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Orden</label>
              <input type="number" value={form.orden} onChange={(e) => setForm({ ...form, orden: parseInt(e.target.value) || 0 })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              {!editId && <p className="text-xs text-gray-400 mt-1">Sugerido: {form.orden} (el más alto + 1)</p>}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Foto (JPG, PNG, WebP)</label>
            <div className="flex items-center gap-3 flex-wrap">
              {preview && !limpiarFoto ? (
                <div className="relative group">
                  <img src={preview} alt="preview" className="w-16 h-16 rounded-lg object-cover border" />
                  <button
                    type="button"
                    onClick={() => { setPreview(null); setLimpiarFoto(true); if (fileRef.current) fileRef.current.value = ""; }}
                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-700 shadow"
                    title="Quitar foto"
                  >×</button>
                </div>
              ) : null}
              {!limpiarFoto && (
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => { const file = e.target.files[0]; if (file) { setPreview(URL.createObjectURL(file)); setLimpiarFoto(false); } }}
                  className="text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100" />
              )}
              {limpiarFoto && (
                <button type="button" onClick={() => setLimpiarFoto(false)}
                  className="text-xs text-indigo-600 hover:underline">Restaurar foto actual</button>
              )}
            </div>
          </div>
          {/* Variantes */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-gray-700">Variantes</label>
              <button type="button" onClick={() => setForm(f => ({ ...f, variantes: [...(f.variantes || []), { nombre: "", opciones: [], _opInput: "" }] }))}
                className="text-xs text-indigo-600 hover:text-indigo-800 font-medium">+ Personalizada</button>
            </div>
            <div className="flex gap-2 mb-2">
              <button type="button"
                onClick={() => addPreset("Talla", ["XS", "S", "M", "L", "XL", "XXL", "XXXL"])}
                className="flex items-center gap-1 text-xs bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-medium px-3 py-1.5 rounded-lg transition-colors">
                <span>👕</span> + Tallas
              </button>
              <button type="button"
                onClick={() => addPreset("Color", ["Rojo", "Azul", "Negro", "Blanco", "Gris", "Verde", "Amarillo", "Rosa"])}
                className="flex items-center gap-1 text-xs bg-purple-50 hover:bg-purple-100 text-purple-700 font-medium px-3 py-1.5 rounded-lg transition-colors">
                <span>🎨</span> + Colores
              </button>
            </div>
            {(form.variantes || []).map((v, vi) => (
              <div key={vi} className="bg-gray-50 rounded-lg p-3 mb-2 border border-gray-200">
                <div className="flex items-center gap-2 mb-2">
                  <input value={v.nombre} onChange={e => {
                    const nvs = [...form.variantes]; nvs[vi] = { ...nvs[vi], nombre: e.target.value };
                    setForm(f => ({ ...f, variantes: nvs }));
                  }} placeholder="Nombre (ej: Talla)" className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                  <button type="button" onClick={() => setForm(f => ({ ...f, variantes: f.variantes.filter((_, i) => i !== vi) }))}
                    className="text-red-400 hover:text-red-600 text-xl leading-none">×</button>
                </div>
                <div className="flex flex-wrap gap-1 mb-2">
                  {(v.opciones || []).map((op, oi) => (
                    <span key={oi} className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded text-xs font-medium">
                      {op}
                      <button type="button" onClick={() => {
                        const nvs = [...form.variantes]; nvs[vi] = { ...nvs[vi], opciones: nvs[vi].opciones.filter((_, i) => i !== oi) };
                        setForm(f => ({ ...f, variantes: nvs }));
                      }} className="text-indigo-400 hover:text-indigo-600 ml-0.5">×</button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-1">
                  <input value={v._opInput || ""} onChange={e => {
                    const nvs = [...form.variantes]; nvs[vi] = { ...nvs[vi], _opInput: e.target.value };
                    setForm(f => ({ ...f, variantes: nvs }));
                  }} onKeyDown={e => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const val = (v._opInput || "").trim();
                      if (val) {
                        const nvs = [...form.variantes]; nvs[vi] = { ...nvs[vi], opciones: [...(nvs[vi].opciones || []), val], _opInput: "" };
                        setForm(f => ({ ...f, variantes: nvs }));
                      }
                    }
                  }} placeholder="Escribe una opcion y presiona Enter" className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                  <button type="button" onClick={() => {
                    const val = (v._opInput || "").trim();
                    if (val) {
                      const nvs = [...form.variantes]; nvs[vi] = { ...nvs[vi], opciones: [...(nvs[vi].opciones || []), val], _opInput: "" };
                      setForm(f => ({ ...f, variantes: nvs }));
                    }
                  }} className="bg-indigo-600 text-white px-3 py-1 rounded text-sm hover:bg-indigo-700">+</button>
                </div>
              </div>
            ))}
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.activo_menu} onChange={(e) => setForm({ ...form, activo_menu: e.target.checked })}
              className="rounded border-gray-300 text-indigo-600" />
            <span className="text-sm text-gray-700">Visible en el menu digital</span>
          </label>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setModal(false)} className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Cancelar</button>
            <button type="submit" className="flex-1 bg-indigo-600 text-white py-2 rounded-lg text-sm hover:bg-indigo-700">
              {editId ? "Guardar cambios" : "Crear producto"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
